import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Trash2, Pencil, Plus, RefreshCw, LogOut, X, Save } from "lucide-react";
import { toast } from "sonner";
import MDEditor from "@uiw/react-md-editor";
import { API } from "../lib/api";
import { useAuth } from "../auth/AuthProvider";
import Monogram from "../components/Monogram";

const EMPTY_POST = {
  slug: "",
  title: "",
  title_fr: "",
  excerpt: "",
  excerpt_fr: "",
  cover: "",
  date: new Date().toISOString().slice(0, 10),
  reading_time: 5,
  body: "",
  body_fr: "",
};

function slugify(str) {
  return (str || "")
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim().replace(/\s+/g, "-").slice(0, 100);
}

export default function AdminBlogPage() {
  const { token, logout, user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null); // null | { mode: "create"|"edit", data, originalSlug }
  const [saving, setSaving] = useState(false);

  const authHeader = { Authorization: `Bearer ${token}` };

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/blog`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPosts(data);
    } catch {
      toast.error("Failed to load posts.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) fetchPosts();
  }, [token, fetchPosts]);

  const openCreate = () => setEditor({ mode: "create", data: { ...EMPTY_POST }, originalSlug: null });
  const openEdit = (p) => setEditor({ mode: "edit", data: { ...p }, originalSlug: p.slug });
  const closeEditor = () => setEditor(null);

  const onField = (k, v) => setEditor((e) => ({ ...e, data: { ...e.data, [k]: v } }));

  const onSave = async () => {
    if (!editor) return;
    const d = editor.data;
    const required = ["slug", "title", "title_fr", "excerpt", "excerpt_fr", "cover", "date", "body", "body_fr"];
    for (const k of required) {
      if (!d[k] || String(d[k]).trim() === "") {
        toast.error(`Field "${k}" is required.`);
        return;
      }
    }
    setSaving(true);
    try {
      const payload = { ...d, reading_time: Number(d.reading_time) || 5 };
      if (editor.mode === "create") {
        await axios.post(`${API}/admin/blog`, payload, { headers: authHeader });
        toast.success("Post created.");
      } else {
        await axios.put(`${API}/admin/blog/${editor.originalSlug}`, payload, { headers: authHeader });
        toast.success("Post updated.");
      }
      closeEditor();
      fetchPosts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally { setSaving(false); }
  };

  const onDelete = async (slug) => {
    if (!window.confirm(`Delete post "${slug}"?`)) return;
    try {
      await axios.delete(`${API}/admin/blog/${slug}`, { headers: authHeader });
      setPosts((arr) => arr.filter((p) => p.slug !== slug));
      toast.success("Post deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  return (
    <main className="min-h-screen bg-[#050505] text-ink-100" data-testid="page-admin-blog">
      <header className="border-b border-hair sticky top-0 z-40 bg-[#050505]/85 backdrop-blur-xl">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10 py-4 flex items-center justify-between">
          <Monogram size={32} withWord />
          <nav className="flex items-center gap-6 text-[11px] tracking-[0.22em] uppercase">
            <Link to="/admin/leads" className="text-ink-300 hover:text-ink-100" data-testid="admin-nav-leads">Leads</Link>
            <Link to="/admin/blog" className="text-ink-100" data-testid="admin-nav-blog">Journal</Link>
            <Link to="/admin/studio" className="text-ink-300 hover:text-ink-100" data-testid="admin-nav-studio">Generator</Link>
            <Link to="/" className="text-ink-500 hover:text-ink-300" data-testid="admin-nav-site">↗ Site</Link>
            <div className="hidden md:flex items-center gap-2 text-ink-500">
              <span className="w-1.5 h-1.5 rounded-full bg-rouge" /> {user?.email}
            </div>
            <button onClick={logout} className="flex items-center gap-2 text-ink-300 hover:text-rouge" data-testid="admin-logout">
              <LogOut size={14} /> Logout
            </button>
          </nav>
        </div>
      </header>

      <section className="mx-auto max-w-[1440px] px-6 lg:px-10 py-16">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-[11px] tracking-[0.28em] uppercase text-ink-300 flex items-center gap-3">
              <span className="block w-8 h-px bg-rouge" /> Journal CMS
            </div>
            <h1 className="mt-4 font-serif-editorial text-5xl lg:text-6xl tracking-tight text-ink-100">
              {posts.length} <span className="text-ink-500">post{posts.length === 1 ? "" : "s"}</span>
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={fetchPosts} className="flex items-center gap-2 border border-hair hover:border-ink-100 px-4 py-2 text-[11px] tracking-[0.22em] uppercase" data-testid="admin-blog-refresh"><RefreshCw size={13} /> Refresh</button>
            <button onClick={openCreate} className="flex items-center gap-2 bg-rouge hover:bg-rouge-deep px-4 py-2 text-[11px] tracking-[0.22em] uppercase text-ink-100" data-testid="admin-blog-create"><Plus size={13} /> New post</button>
          </div>
        </div>

        {loading ? (
          <div className="text-ink-500 text-sm">Loading…</div>
        ) : posts.length === 0 ? (
          <div className="border border-hair p-16 text-center" data-testid="admin-blog-empty">
            <div className="text-ink-500 text-[11px] tracking-[0.22em] uppercase">No posts yet</div>
            <p className="mt-3 text-ink-300 text-sm">Create your first journal entry from the button above.</p>
          </div>
        ) : (
          <div className="border border-hair">
            <div className="grid grid-cols-12 gap-4 px-5 py-3 text-[10px] tracking-[0.22em] uppercase text-ink-500 border-b border-hair">
              <div className="col-span-1">Cover</div>
              <div className="col-span-5">Title / Slug</div>
              <div className="col-span-2">Date</div>
              <div className="col-span-1">Min</div>
              <div className="col-span-3 text-right">Actions</div>
            </div>
            {posts.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i, 10) * 0.03 }}
                data-testid={`admin-blog-row-${i}`}
                className="grid grid-cols-12 gap-4 items-center px-5 py-4 border-b border-hair last:border-b-0 hover:bg-[#0c0c0e] transition-colors"
              >
                <div className="col-span-1 aspect-[4/3] overflow-hidden bg-surface">
                  <img src={p.cover} alt="" className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="col-span-5">
                  <div className="text-ink-100 text-sm line-clamp-1">{p.title}</div>
                  <div className="text-ink-500 text-xs mt-1 font-mono">{p.slug}</div>
                </div>
                <div className="col-span-2 text-ink-300 text-xs">{p.date}</div>
                <div className="col-span-1 text-ink-300 text-xs">{p.reading_time}</div>
                <div className="col-span-3 flex items-center justify-end gap-2">
                  <Link to={`/journal/${p.slug}`} target="_blank" className="text-ink-500 hover:text-ink-100 text-[10px] tracking-[0.22em] uppercase" data-testid={`admin-blog-view-${p.slug}`}>View</Link>
                  <button onClick={() => openEdit(p)} className="flex items-center gap-1.5 border border-hair hover:border-ink-100 px-3 py-1.5 text-[10px] tracking-[0.22em] uppercase" data-testid={`admin-blog-edit-${p.slug}`}><Pencil size={11} /> Edit</button>
                  <button onClick={() => onDelete(p.slug)} className="flex items-center gap-1.5 border border-hair hover:border-rouge hover:text-rouge px-3 py-1.5 text-[10px] tracking-[0.22em] uppercase" data-testid={`admin-blog-delete-${p.slug}`}><Trash2 size={11} /> Delete</button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      <AnimatePresence>
        {editor && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-stretch justify-center p-0 md:p-6 overflow-y-auto"
            onClick={closeEditor}
            data-testid="admin-blog-editor"
          >
            <motion.div
              initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }}
              className="w-full max-w-4xl bg-[#0c0c0e] border border-hair p-6 md:p-10 my-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-4 mb-8">
                <div>
                  <div className="text-[10px] tracking-[0.24em] uppercase text-rouge">{editor.mode === "create" ? "New entry" : "Editing"}</div>
                  <h2 className="mt-2 font-serif-editorial text-3xl text-ink-100 leading-tight">{editor.data.title || "Untitled"}</h2>
                </div>
                <button onClick={closeEditor} className="text-ink-500 hover:text-ink-100 p-2" data-testid="admin-blog-editor-close"><X size={18} /></button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Title (EN)" testid="field-title">
                  <input value={editor.data.title} onChange={(e) => {
                    onField("title", e.target.value);
                    if (editor.mode === "create" && (!editor.data.slug || editor.data.slug === slugify(editor.data.title))) {
                      onField("slug", slugify(e.target.value));
                    }
                  }} className={inputCls} />
                </Field>
                <Field label="Titre (FR)" testid="field-title-fr">
                  <input value={editor.data.title_fr} onChange={(e) => onField("title_fr", e.target.value)} className={inputCls} />
                </Field>
                <Field label="Slug (URL)" testid="field-slug">
                  <input value={editor.data.slug} onChange={(e) => onField("slug", slugify(e.target.value))} className={`${inputCls} font-mono`} />
                </Field>
                <Field label="Cover image URL" testid="field-cover">
                  <input value={editor.data.cover} onChange={(e) => onField("cover", e.target.value)} className={inputCls} placeholder="/generated/… or https://…" />
                </Field>
                <Field label="Date (YYYY-MM-DD)" testid="field-date">
                  <input type="date" value={editor.data.date} onChange={(e) => onField("date", e.target.value)} className={inputCls} />
                </Field>
                <Field label="Reading time (minutes)" testid="field-reading-time">
                  <input type="number" min="1" max="120" value={editor.data.reading_time} onChange={(e) => onField("reading_time", e.target.value)} className={inputCls} />
                </Field>
                <Field label="Excerpt (EN)" testid="field-excerpt" className="md:col-span-2">
                  <textarea rows={2} value={editor.data.excerpt} onChange={(e) => onField("excerpt", e.target.value)} className={inputCls} />
                </Field>
                <Field label="Extrait (FR)" testid="field-excerpt-fr" className="md:col-span-2">
                  <textarea rows={2} value={editor.data.excerpt_fr} onChange={(e) => onField("excerpt_fr", e.target.value)} className={inputCls} />
                </Field>
                <Field label="Body (EN) — Markdown" testid="field-body" className="md:col-span-2">
                  <div data-color-mode="dark" data-testid="mdeditor-en">
                    <MDEditor
                      value={editor.data.body}
                      onChange={(v) => onField("body", v || "")}
                      height={360}
                      preview="live"
                      visibleDragbar={false}
                    />
                  </div>
                </Field>
                <Field label="Corps (FR) — Markdown" testid="field-body-fr" className="md:col-span-2">
                  <div data-color-mode="dark" data-testid="mdeditor-fr">
                    <MDEditor
                      value={editor.data.body_fr}
                      onChange={(v) => onField("body_fr", v || "")}
                      height={360}
                      preview="live"
                      visibleDragbar={false}
                    />
                  </div>
                </Field>
              </div>

              <div className="mt-8 flex items-center justify-end gap-3">
                <button onClick={closeEditor} className="border border-hair text-ink-300 hover:text-ink-100 px-5 py-2.5 text-[11px] tracking-[0.22em] uppercase" data-testid="admin-blog-editor-cancel">Cancel</button>
                <button onClick={onSave} disabled={saving} className="flex items-center gap-2 bg-rouge hover:bg-rouge-deep disabled:opacity-60 px-5 py-2.5 text-[11px] tracking-[0.22em] uppercase text-ink-100" data-testid="admin-blog-editor-save">
                  <Save size={13} /> {saving ? "Saving…" : (editor.mode === "create" ? "Publish" : "Save changes")}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}

const inputCls = "w-full bg-[#050505] border border-hair focus:border-rouge outline-none px-3 py-2.5 text-sm text-ink-100 placeholder:text-ink-500 transition-colors";

function Field({ label, children, className = "", testid }) {
  return (
    <label className={`block ${className}`} data-testid={testid}>
      <span className="block text-[10px] tracking-[0.24em] uppercase text-ink-500 mb-2">{label}</span>
      {children}
    </label>
  );
}
